import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Cookie, ExternalLink } from 'lucide-react';

const PrivacyConsentBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Show cookie banner on every page load/reload
    const hasConsentedThisSession = sessionStorage.getItem('cookie_consent_session');
    if (!hasConsentedThisSession) {
      const timer = setTimeout(() => setShowBanner(true), 100);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      essential: true,
      functional: true,
      timestamp: new Date().toISOString(),
      type: 'all'
    };
    sessionStorage.setItem('cookie_consent_session', 'all');
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };

  const handleEssentialOnly = () => {
    const consentData = {
      essential: true,
      functional: false,
      timestamp: new Date().toISOString(),
      type: 'essential'
    };
    sessionStorage.setItem('cookie_consent_session', 'essential');
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg animate-in slide-in-from-bottom-4 duration-300">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-full">
              <Cookie className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-lg font-heading font-bold text-foreground">
              {t('cookies.bannerTitle')}
            </h2>
          </div>

          {/* Content */}
          <p className="text-sm text-muted-foreground mb-4">
            {t('cookies.bannerDescription')}
          </p>

          {/* Read Policy Link */}
          <Link 
            to="/cookies-policy" 
            target="_blank"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-6"
          >
            {t('cookies.readPolicy')}
            <ExternalLink className="w-3 h-3" />
          </Link>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleEssentialOnly}
              variant="outline"
              className="flex-1"
            >
              {t('cookies.essentialOnly')}
            </Button>
            <Button
              onClick={handleAcceptAll}
              className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {t('cookies.acceptAll')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyConsentBanner;
